import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import { articles, categories } from "@/mocks/articles";

export default function Article() {
  const { slug = "" } = useParams();
  const article = articles.find((a) => a.slug === slug);

  usePageMeta({
    title: article
      ? `${article.title}_${article.catName}_每日黑料人物盘点`
      : "内容不存在_每日黑料人物盘点",
    description: article
      ? `${article.lead} 这是每日黑料「${article.catName}」栏目的深度盘点文章，围绕${article.tags.join(
          "、"
        )}展开，看人物内幕与话题走向。`
      : "你访问的内容不存在，请返回首页浏览更多每日黑料人物盘点。",
    keywords: article
      ? `每日黑料,${article.catName},${article.title},${article.tags.join(",")},公众人物盘点`
      : "每日黑料,公众人物盘点",
    canonicalPath: `/article/${slug}`,
    type: "article",
  });

  useJsonLd(
    article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.lead,
            datePublished: article.date,
            inLanguage: "zh-CN",
            articleSection: article.catName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "每日黑料编辑部" },
            publisher: { "@type": "Organization", name: "每日黑料" },
            image: article.cover,
            isPartOf: {
              "@type": "CollectionPage",
              url: `/category/${article.catKey}`,
              name: `${article.catName} - 每日黑料`,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: article.catName, item: `/category/${article.catKey}` },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : []
  );

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <p className="text-6xl font-label text-primary-500">404</p>
        <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950">这篇盘点已下架或不存在</h1>
        <p className="mt-3 text-foreground-600">信息经过复核后可能被撤下，或地址有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>返回首页
        </Link>
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const catMeta = categories.find((c) => c.key === article.catKey);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-600" aria-label="面包屑">
        <Link to="/" className="flex items-center gap-1 hover:text-primary-700">
          <i className="ri-home-5-line"></i>首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${article.catKey}`} className="hover:text-primary-700">
          {article.catName}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="max-w-[220px] truncate text-primary-700">{article.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-3">
        {/* 正文 */}
        <article className="lg:col-span-2">
          <span className="inline-flex items-center gap-2 rounded-sm bg-primary-500 px-2 py-0.5 font-label text-xs tracking-widest text-background-50">
            {article.catName} · 深度盘点
          </span>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 border-y border-foreground-900/10 py-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-edit-2-line text-primary-600"></i>
              每日黑料编辑部
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views}阅读
            </span>
            <span className="ml-auto flex items-center gap-2">
              <i className="ri-time-line"></i>建议阅读约 4 分钟
            </span>
          </div>

          <div className="mt-5 overflow-hidden rounded-lg border border-foreground-900/10">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} 盘点配图`}
              className="aspect-[16/9] w-full object-cover object-top"
            />
          </div>

          <p className="mt-7 rounded-md border-l-4 border-primary-500 bg-primary-50/70 px-5 py-4 text-lg font-medium leading-8 text-foreground-800">
            {article.lead}
          </p>

          <div className="mt-5 space-y-5">
            {article.body.map((p, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-800">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-foreground-900/10 pt-6">
            <span className="text-sm text-foreground-600">相关标签：</span>
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/category/${article.catKey}`}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                #{t}
              </Link>
            ))}
          </div>

          {/* 内容提示 */}
          <div className="mt-8 flex items-start gap-3 rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
            <i className="ri-shield-check-line mt-0.5 text-xl text-primary-600"></i>
            <div className="text-sm leading-6 text-foreground-600">
              <p className="font-medium text-foreground-900">编辑说明</p>
              <p className="mt-1">
                本文为基于公开信息的观察与盘点，不对任何个人作出法律意义上的定论。文中涉及的事实时序与说法，均以官方与当事方的最终回应为准；如有补充线索，欢迎通过
                <Link to="/contact" className="mx-1 text-primary-700 underline underline-offset-2">读者联络</Link>提交核实。
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to={`/category/${article.catKey}`}
              className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-medium text-primary-800 transition-colors hover:bg-primary-100"
            >
              <i className="ri-arrow-left-line"></i>返回 {article.catName}
            </Link>
            <span className="text-xs text-foreground-500">
              {catMeta ? `收录于「${catMeta.name}」栏目` : ""}
            </span>
          </div>
        </article>

        {/* 侧栏 */}
        <aside className="space-y-8">
          <div className="rounded-lg border border-foreground-900/10 bg-foreground-950 p-5 text-background-100">
            <p className="font-label text-xs tracking-[0.3em] text-accent-400">EDITION</p>
            <h3 className="mt-2 font-heading text-xl font-black text-background-50">本期栏目</h3>
            <p className="mt-2 text-sm leading-6 text-background-200">
              {catMeta ? catMeta.desc : ""}
            </p>
            <Link
              to={`/category/${article.catKey}`}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-600"
            >
              进入栏目<i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
            <h3 className="font-heading text-lg font-black text-foreground-950">编辑精选</h3>
            <div className="mt-2 space-y-0">
              {related.slice(0, 4).map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex items-start gap-3 border-b border-foreground-900/10 py-3 last:border-0"
                >
                  <span className="mt-1.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-accent-100 font-label text-sm text-accent-800">
                    <i className="ri-star-line"></i>
                  </span>
                  <span className="line-clamp-2 text-sm leading-6 text-foreground-800 transition-colors group-hover:text-primary-700">
                    {a.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* 相关阅读 */}
      <section className="mt-12 border-t border-foreground-900/10 pt-8" aria-label="相关阅读">
        <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">更多盘点阅读</h2>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((a) => (
            <ArticleCard key={a.slug} article={a} layout="grid" />
          ))}
        </div>
      </section>
    </div>
  );
}